import { 
  Bike, 
  Plug, 
  Snowflake, 
  Wifi, 
  Ticket, 
  CheckCircle, 
  UtensilsCrossed, 
  TriangleRight, 
  Accessibility, 
  Printer, 
  Wine, 
  Baby,
  LucideIcon
} from 'lucide-react';
import { StyledTooltip } from '../TooltipStyles';
import styled from 'styled-components';
import { colors } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

// Styled wrapper for Lucide icons to match the size and styling
const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 14px;
  width: 14px;
  color: ${colors.grey600};
  
  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    fill: none;
  }
`;

type Props = {
  code?: string
}

// Map amenity codes to icon names (for deduplication)
// Multiple codes can map to the same icon name
export const getIconNameForCode = (code: string): string | null => {
  const iconNameMap: { [key: string]: string } = {
    'bicycle-conveyance': 'Bike',
    'bicycle-conveyance-reservation': 'Bike',
    'power-sockets': 'Plug',
    'air-conditioned': 'Snowflake',
    'wifi': 'Wifi',
    'compulsory-reservation': 'Ticket',
    'komfort-checkin': 'CheckCircle',
    'on-board-restaurant': 'UtensilsCrossed',
    'boarding-ramp': 'TriangleRight',
    'ER': 'TriangleRight',
    'EA': 'Accessibility',
    'FM': 'Printer',
    'OR': 'Ticket',
    'BW': 'Wine',
    'SA': 'Baby',
    'FG': 'Ticket',
    '97': 'Accessibility'
  };
  
  return iconNameMap[code] || null;
}

// Map amenity codes to Lucide icons
const icons: { [x: string]: LucideIcon } = {
  'bicycle-conveyance': Bike,
  'bicycle-conveyance-reservation': Bike,
  'power-sockets': Plug,
  'air-conditioned': Snowflake,
  'wifi': Wifi,
  'compulsory-reservation': Ticket,
  'komfort-checkin': CheckCircle,
  'on-board-restaurant': UtensilsCrossed,
  'boarding-ramp': TriangleRight,
  'ER': TriangleRight,
  'EA': Accessibility,
  'FM': Printer,
  'OR': Ticket,
  'BW': Wine,
  'SA': Baby,
  'FG': Ticket,
  '97': Accessibility
}

const AmenityIcon = ({ code }: Props) => {
  const { t } = useTranslation();
  
  if (!code) return null;
  const IconComponent = icons[code];

  if (!IconComponent) return null;

  // Map amenity codes to translation keys
  const tooltipKeyMap: { [x: string]: string } = {
    'bicycle-conveyance': 'journeyDetails.amenities.bicycleConveyance',
    'bicycle-conveyance-reservation': 'journeyDetails.amenities.bicycleConveyanceReservation',
    'power-sockets': 'journeyDetails.amenities.powerSockets',
    'air-conditioned': 'journeyDetails.amenities.airConditioned',
    'wifi': 'journeyDetails.amenities.wifi',
    'compulsory-reservation': 'journeyDetails.amenities.compulsoryReservation',
    'komfort-checkin': 'journeyDetails.amenities.komfortCheckin',
    'on-board-restaurant': 'journeyDetails.amenities.onBoardRestaurant',
    'boarding-ramp': 'journeyDetails.amenities.boardingRamp',
    'ER': 'journeyDetails.amenities.boardingRamp',
    'EA': 'journeyDetails.amenities.wheelchairAccessible',
    'FM': 'journeyDetails.amenities.ticketMachineInside',
    'OR': 'journeyDetails.amenities.compulsoryReservation',
    'BW': 'journeyDetails.amenities.bar',
    'SA': 'journeyDetails.amenities.nursingRoom',
    'FG': 'journeyDetails.amenities.compulsoryReservation',
    '97': 'journeyDetails.amenities.wheelchairAccessible'
  };

  const tooltipKey = tooltipKeyMap[code];
  const tooltipText = tooltipKey ? t(tooltipKey) : t('journeyDetails.amenities.unknownAmenity');

  return (
    <>
      <IconWrapper
        data-tooltip-id={`tooltip-${code}`}
        data-tooltip-content={tooltipText}
      >
        <IconComponent size={14} strokeWidth={2} />
      </IconWrapper>
      <StyledTooltip id={`tooltip-${code}`} place="top" className="tooltip-styled" />
    </>
  );
};

export default AmenityIcon;